import React, { useContext } from "react";

import { FiltersSelected, FiltersSelectedProps } from "@usereactify/search";

import { FilterContext } from "~/providers/FilterProvider";

import { Button, IconCross } from "@bared/ui";

type RenderFiltersSelectedProps = React.ComponentProps<
  NonNullable<FiltersSelectedProps["render"]>
>;

type FilterSectionSelectedFiltersProps = {
  layout?: "scroll" | "wrap";
};

type FiltersSelectedItemProps = {
  selectedFilter: RenderFiltersSelectedProps["selectedFilters"][number];
  handleRemove: RenderFiltersSelectedProps["handleRemove"];
};

type ValueWithLabel = { label: string };
type ValueWithLabelArray = ValueWithLabel[];
type HandleRemove = (key: string, value: string[]) => void;

type SingleRangeViewType = {
  selectedKey: string;
  value: ValueWithLabel;
  handleRemove: HandleRemove;
};
type SingleNOTRangeViewType = {
  selectedKey: string;
  value: string;
  handleRemove: HandleRemove;
};
type MultiRangeViewType = {
  selectedKey: string;
  value: ValueWithLabel;
  handleRemove: HandleRemove;
};
type MultiNOTRangeViewType = {
  selectedKey: string;
  value: string;
  handleRemove: HandleRemove;
};
type SliderViewType = { displaySliderPrefix: string; value: string[] };

const SingleRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: SingleRangeViewType) => (
  <li
    key={selectedKey}
    data-testid={selectedKey}
    className="flex-shrink-0 justify-center gap-[10px]"
  >
    <Button
      variant="plain"
      size="xsmall"
      className="relative flex cursor-pointer border px-micro py-micro text-center text-hint leading-input"
      onClick={() => handleRemove(selectedKey, [value.label])}
    >
      <span className="block capitalize">{value.label}</span>
      <IconCross className="block w-mini pl-[10px]" />
    </Button>
  </li>
);

const SingleNOTRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: SingleNOTRangeViewType) => (
  <li
    key={selectedKey}
    data-testid={selectedKey}
    className="flex-shrink-0 justify-center gap-[10px]"
  >
    <Button
      variant="plain"
      size="xsmall"
      className="relative flex cursor-pointer gap-micro border px-micro py-micro text-center text-hint leading-input"
      onClick={() => handleRemove(selectedKey, [value])}
    >
      <span className="block capitalize">{value}</span>
      <IconCross className="block w-[10px]" />
    </Button>
  </li>
);

const MultiRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: MultiRangeViewType) => (
  <li
    className="flex-shrink-0 justify-center gap-[10px]"
    key={selectedKey + value?.label}
    data-testid={selectedKey + "-" + value?.label}
  >
    <Button
      variant="plain"
      size="xsmall"
      className="relative flex cursor-pointer gap-micro border px-micro py-micro text-center text-hint leading-input"
      onClick={() => handleRemove(selectedKey, [value.label])}
    >
      <span className="block capitalize">{value.label}</span>
      <IconCross className="block w-[10px]" />
    </Button>
  </li>
);

const MultiNOTRangeView = ({
  selectedKey,
  value,
  handleRemove,
}: MultiNOTRangeViewType) => (
  <li
    className="flex-shrink-0 justify-center gap-[10px]"
    key={selectedKey + value}
    data-testid={selectedKey + "-" + value}
  >
    <Button
      variant="plain"
      size="xsmall"
      className="relative flex cursor-pointer gap-micro border px-micro py-micro text-center text-hint leading-input"
      onClick={() => handleRemove(selectedKey, [value])}
    >
      <span className="block capitalize">{value}</span>
      <IconCross className="block w-[10px]" />
    </Button>
  </li>
);

const SliderView = ({ displaySliderPrefix, value }: SliderViewType) => (
  <li
    className="flex-shrink-0 justify-center gap-[10px]"
    key={"key-" + displaySliderPrefix}
    data-testid={displaySliderPrefix}
  >
    <Button
      variant="plain"
      size="xsmall"
      className="relative flex cursor-pointer gap-micro border px-micro py-micro text-center text-hint leading-input"
    >
      <span className="block capitalize">{`${displaySliderPrefix}${value?.[0]} to ${displaySliderPrefix}${value?.[1]}`}</span>
      <IconCross className="block w-[10px]" />
    </Button>
  </li>
);

const FiltersSelectedItem: React.FC<FiltersSelectedItemProps> = (props) => {
  const { filters } = useContext(FilterContext);
  const filter = filters?.find(
    (filter) => filter.handle === props.selectedFilter.key,
  );
  const { key, value } = props?.selectedFilter || {};
  const { displayType, displayView, displaySliderPrefix } = filter || {};

  return (
    <>
      {displayType === "single" && displayView === "range" && (
        <SingleRangeView
          selectedKey={key}
          value={value as ValueWithLabel}
          handleRemove={props.handleRemove}
        />
      )}
      {displayType === "single" && displayView !== "range" && (
        <SingleNOTRangeView
          selectedKey={key}
          value={value as string}
          handleRemove={props.handleRemove}
        />
      )}
      {displayType === "multi" &&
        displayView === "range" &&
        (value as ValueWithLabelArray)?.map((val) => (
          <MultiRangeView
            key={key}
            selectedKey={key}
            value={val}
            handleRemove={props.handleRemove}
          />
        ))}
      {displayType === "multi" &&
        displayView !== "range" &&
        (value as string[])?.map((val) => (
          <MultiNOTRangeView
            key={key}
            selectedKey={key}
            value={val}
            handleRemove={props.handleRemove}
          />
        ))}
      {displayType === "slider" && (
        <SliderView
          displaySliderPrefix={displaySliderPrefix as string}
          value={value as string[]}
        />
      )}
    </>
  );
};

const RenderFiltersSelectedScroll: React.FC<RenderFiltersSelectedProps> = (
  props,
) => {
  return (
    <div className="before:bg-[linear-gradient(to right, rgba(243,241,237,0) 0%,rgba(243,241,237,1) 100%)] flex flex-col before:pointer-events-none before:absolute before:right-zero before:z-10 before:block before:h-mega before:w-mega">
      <ul
        className="hide-scrollbar flex w-full list-none space-x-[10px] overflow-x-auto overflow-y-hidden"
        data-testid="selected-scroll-wrapper"
      >
        {props.selectedFilters?.map((selectedFilter) => (
          <FiltersSelectedItem
            key={selectedFilter?.key}
            selectedFilter={selectedFilter}
            handleRemove={props.handleRemove}
          />
        ))}
      </ul>
    </div>
  );
};

const RenderFiltersSelected: React.FC<RenderFiltersSelectedProps> = (props) => {
  return (
    <div className="flex w-full flex-col lg:hidden">
      <span className="block py-mini text-left font-medium">
        Filters Applied:
      </span>
      <div
        className="flex list-none flex-wrap place-items-end gap-[10px] overflow-x-hidden overflow-y-hidden"
        data-testid="selected-default-wrapper"
      >
        {props.selectedFilters?.map((selectedFilter) => (
          <FiltersSelectedItem
            key={selectedFilter?.key}
            selectedFilter={selectedFilter}
            handleRemove={props.handleRemove}
          />
        ))}
      </div>
    </div>
  );
};

export const FilterSectionSelectedFilters = (
  props: FilterSectionSelectedFiltersProps,
) => {
  return (
    <FiltersSelected
      render={
        props.layout === "scroll"
          ? RenderFiltersSelectedScroll
          : RenderFiltersSelected
      }
    />
  );
};
